import { useCallback } from 'react';
import { useNavigate, type NavigateOptions } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

// Utility functions for handling authentication across the application

export const isAuthError = (error: Error | unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('session has expired') || 
         message.includes('Please log in again') ||
         message.includes('Authentication token is required') ||
         message.includes('Not authorized') ||
         message.includes('Unauthorized') ||
         message.toLowerCase().includes('jwt expired') ||
         message.toLowerCase().includes('invalid token');
};

export const isTokenExpiredError = (error: Error | unknown): boolean => {
  const message = error instanceof Error ? error.message : String(error);
  return message.includes('session has expired') || 
         message.includes('Please log in again') ||
         message.toLowerCase().includes('jwt expired');
};

// Helper function to create consistent auth error handlers
export const createAuthErrorHandler = (logout: () => void, navigate: (path: string, options?: NavigateOptions) => void) => {
  return (error: Error): boolean => {
    if (isTokenExpiredError(error)) {
      console.log('Session expired, redirecting to login...');
      logout();
      navigate('/login', { replace: true });
      return true; // Error was handled
    }
    return false; // Error was not handled
  };
};

/**
 * Custom hook to handle authentication errors consistently across the app.
 * Use this in any component that makes API calls.
 * 
 * Usage:
 * const { handleAuthError } = useAuthErrorHandler();
 * 
 * try {
 *   const data = await someApiCall();
 * } catch (error) {
 *   if (!handleAuthError(error)) {
 *     // Handle non-auth errors
 *     setError(error.message);
 *   }
 * }
 */
export const useAuthErrorHandler = () => {
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleAuthError = useCallback((error: unknown): boolean => {
    if (isAuthError(error)) {
      console.warn('Auth error detected, logging out...');
      logout();
      navigate('/login', { replace: true });
      return true; // Error was handled
    }
    return false; // Error was not an auth error
  }, [logout, navigate]);

  return { handleAuthError };
}; 